import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActionType,
  IntegrationTermsAndConditions,
  IntergrationInstructionStep,
  StrVal
} from '../../../lib/api/account-linking/types';
import Step, { StepRow } from './Step';
import { IsIntegrationValid } from '../lib/helpers/ValidateIntegration';
import { getDefaultInputValue } from '../lib/helpers/StepInputOptions';
import TermsLabel from './TermsLabel';
import { P2GGrid, P2GFade, P2GBox, P2GButton, P2GLink } from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

interface props {
  action: ActionType;
  integrationSlug: string;
  integrationType: string;
  steps: IntergrationInstructionStep[];
  hasInputs: boolean;
  showInstructions: boolean;
  saving: boolean;
  readonly: boolean;
  onSubmit: (values: StrVal) => void;
  terms: IntegrationTermsAndConditions;
  onFileUploadSubmission?: () => void;
  submissionButtonLabel?: string;
  wrapper: (
    content: React.ReactNode,
    title: string,
    subtitle?: string,
    sectionHeading?: string,
    icon?: React.ReactNode
  ) => React.ReactNode;
}

const ScreenSteps: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.ACCOUNT_LINKING);
  const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
  const [values, setValues] = useState<StrVal>({});
  const [loading, setLoading] = useState(true);

  const activeStep = useMemo(() => {
    return props.steps[activeStepIndex];
  }, [props.steps, activeStepIndex]);

  useEffect(() => {
    const initialValues: StrVal = {};
    props.steps?.forEach((s) => {
      s?.inputs.forEach((i) => {
        if (i.type === 'ExternalFile' || initialValues[i.propertyName]) {
          return;
        }
        const seed = i.value || getDefaultInputValue(i);
        if (seed) {
          initialValues[i.propertyName] = seed;
        }
      });
    });
    setValues(initialValues);
    setLoading(false);
  }, [props.steps, props.terms]);

  const onUpdate = useCallback((key: string, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  const isValid = useMemo(() => {
    return IsIntegrationValid(
      values,
      props.steps.filter((x, i) => i <= activeStepIndex),
      props.terms
    );
  }, [values, props.steps, props.terms, activeStepIndex]);

  const showSubmit = !props.readonly && (activeStepIndex == props.steps?.length - 1 || props.steps?.length === 0);

  if (loading) {
    return null;
  }

  return props.wrapper(
    <P2GGrid container spacing={1} direction="column" key={`${props.showInstructions}`}>
      {/* Fake Screens */}
      {props.steps.map((s, index) => {
        if (activeStepIndex !== index) {
          return null;
        }
        return (
          <React.Fragment key={`${index}`}>
            <P2GGrid item xs={12}>
              <P2GFade in timeout={(index + 1) * 550}>
                <Step
                  saving={props.saving}
                  step={s}
                  showInstructions={props.showInstructions}
                  showNumber={false}
                  stepNumber={index + 1}
                  values={values}
                  onUpdate={onUpdate}
                  integrationType={props.integrationType}
                  integrationSlug={props.integrationSlug}
                  action={props.action}
                  onFileUploadSubmission={props.onFileUploadSubmission}
                />
              </P2GFade>
            </P2GGrid>
            {!props.readonly && (
              <>
                {/* Next Button */}
                {index < props.steps.length - 1 && (
                  <P2GGrid item>
                    <StepRow
                      left={false}
                      right={
                        <P2GBox mt={props.hasInputs ? 2 : 0}>
                          <P2GButton
                            disabled={!isValid}
                            onClick={() => setActiveStepIndex((a) => a + 1)}
                            variant="primary"
                            fullWidth
                            size="large"
                          >
                            {t('NEXT_BUTTON', { defaultValue: 'Next' })}
                          </P2GButton>
                        </P2GBox>
                      }
                    />
                  </P2GGrid>
                )}
                {/* Back Button */}
                {index > 0 && (
                  <P2GGrid item>
                    <P2GBox textAlign="center">
                      <P2GLink onClick={() => setActiveStepIndex((a) => a - 1)}>
                        {t('BACK_BUTTON', { defaultValue: 'Back' })}
                      </P2GLink>
                    </P2GBox>
                  </P2GGrid>
                )}
              </>
            )}
          </React.Fragment>
        );
      })}

      {/* Submit Button */}
      {showSubmit && (
        <P2GGrid item>
          <StepRow
            left={false}
            right={
              <>
                {props.terms && (
                  <P2GBox mt={props.hasInputs ? 2 : 0}>
                    <TermsLabel
                      disabled={props.saving}
                      terms={props.terms}
                      value={values[props.terms.input.propertyName]}
                      onUpdate={onUpdate}
                      action={props.action}
                      integrationSlug={props.integrationSlug}
                      integrationType={props.integrationType}
                    />
                  </P2GBox>
                )}
                <P2GBox mt={props.hasInputs ? 2 : 0}>
                  <P2GButton
                    disabled={!isValid || props.saving}
                    loading={props.saving}
                    onClick={() => props.onSubmit(values)}
                    variant="primary"
                    fullWidth
                    size="large"
                  >
                    {props.submissionButtonLabel || (
                      <Trans
                        t={t}
                        i18nKey={`${props.integrationSlug}_${props.action}_SUBMIT_BUTTON`.toUpperCase()}
                        defaults="{{action}} Account"
                        values={{ action: props.action }}
                      />
                    )}
                  </P2GButton>
                </P2GBox>
              </>
            }
          />
        </P2GGrid>
      )}
    </P2GGrid>,
    activeStep?.title?.content,
    activeStep?.content?.[0]?.content,
    activeStep?.progressLabel?.content
  );
};

export default ScreenSteps;
